import React from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { routerActions } from 'react-router-redux'
import { Container, Loader } from 'semantic-ui-react'
import { get } from 'lodash'
import * as analytics from '../analytics'
import uuid from 'uuid/v4'

import * as UI from '../components'
import * as constants from '../constants'
import { userIsAuthenticated } from '../behaviors'
import { formatCoin, formatUSD } from '../helpers'

export const LOADING_STATES = {
  submitting: 'Submitting reservation',
  awaiting: 'Awaiting reservation confirmation',
  retrieving: 'Retrieving reservation',
  reserving: 'Reserving tokens',
  checkout: 'Checking out address',
  ready: null,
}

export const NORMAL_TIME = 8000
export const MAX_TIME = 30000

export class Invoice extends React.Component {
  static defaultProps = {
    normalTime: NORMAL_TIME,
    maxTime: MAX_TIME,
  }

  state = {
    dollarValue: '-',
    purchaseNotFound: false,
    indeterminate: false,
    invoiceId: uuid(),
  }

  componentDidMount() {
    this.startTimers()
    analytics.incrementPurchaseRenders(this.props.params.purchaseId)
    analytics.timestampPurchase(
      this.props.params.purchaseId,
      LOADING_STATES.submitting
    )
  }

  componentWillReceiveProps(nextProps) {
    const { invoiceId } = this.state
    const { purchase, profile } = nextProps

    const {
      addressDisplayed,
      redirectToReceipt,
      redirectToExpired,
      purchaseUpdated,
      profile: prevProfile,
    } = this.props

    if (profile.purchases && profile.purchases !== prevProfile.purchases) {
      // When the purchase gets added to the user purchases collection, we can be certain
      // that it exists in the database.  Otherwise, just `watch`ing the purchase/purchaseId
      // route will cause a firebase error if you it fires before the purchase has been created
      this.props.manuallyFetchPurchase()
    }

    if (purchase) {
      if (purchase.status === constants.purchaseStatuses.CONFIRMED) {
        redirectToReceipt(invoiceId)
      }
      if (purchase.status === constants.purchaseStatuses.COMPLETE) {
        redirectToReceipt(invoiceId)
      }
      if (purchase.status === constants.purchaseStatuses.EXPIRED) {
        redirectToExpired(invoiceId)
      }

      this.setState({
        dollarValue: Math.floor(purchase.cost * purchase.exchangeRate),
        maxPurchase: purchase.tranche.priceUSD,
      })
    }

    // track various purchase states
    const prevLoadingState = this.getLoadingState()
    const nextLoadingState = this.getLoadingState(nextProps)
    if (prevLoadingState !== nextLoadingState) {
      purchaseUpdated(nextLoadingState, purchase, invoiceId)

      if (nextLoadingState === null) {
        addressDisplayed(purchase, invoiceId)
      }
    }
  }

  componentWillUnmount() {
    this.endTimers()
  }

  getLoadingState = (props = this.props) => {
    const { purchase, location } = props
    const { submitting } = location.state || {}

    if (submitting && !purchase) return LOADING_STATES.submitting
    if (submitting && !purchase.status) return LOADING_STATES.awaiting
    if (!submitting && (!purchase || !purchase.status))
      return LOADING_STATES.retrieving
    if (purchase.status === constants.purchaseStatuses.RESERVING)
      return LOADING_STATES.reserving
    if (purchase.status === constants.purchaseStatuses.READY)
      return LOADING_STATES.ready
  }

  startTimers = () => {
    const {
      maxTime,
      normalTime,
      purchaseNotFound,
      manuallyFetchPurchase,
    } = this.props

    this.normalTimeout = setTimeout(() => {
      if (this.getLoadingState())
        this.setState({
          indeterminate: true,
        })
    }, normalTime)

    this.maxTimeout = setTimeout(() => {
      if (this.getLoadingState())
        this.setState(
          {
            purchaseNotFound: true,
          },
          () => {
            purchaseNotFound()
            // This serves no functional purpose besides a last ditch
            // attempt to find the purchase.
            manuallyFetchPurchase()
          }
        )
    }, maxTime)
  }

  endTimers = () => {
    ;[this.normalTimeout, this.maxTimeout].map(clearTimeout)
  }

  handleChange = evt => {
    const re = /^[0-9\b]+$/
    if (
      evt.target.value === '' ||
      (re.test(evt.target.value) && evt.target.value <= this.state.maxPurchase)
    ) {
      this.setState({ dollarValue: evt.target.value })
    }
  }

  render() {
    const loadingState = this.getLoadingState()
    const { indeterminate, purchaseNotFound } = this.state
    const { handleClickCancel, purchase, storefront } = this.props

    // TODO: Check against a value in storefront.currencies.${purchase.currency}
    const paymentAddress =
      purchase && purchase.currency === 'XLM'
        ? get(storefront, 'currencies.XLM.paymentAddress')
        : purchase && purchase.paymentAddress

    // TODO: Check against a value in storefront.currencies.${purchase.currency}
    const paymentMemo =
      purchase && purchase.currency === 'XLM'
        ? purchase && purchase.paymentAddress
        : null

    if (purchaseNotFound) {
      return (
        <UI.Card>
          <UI.Message type="error">
            We could not find the purchase order. Please create a new one{' '}
            <UI.Link to="/">here</UI.Link>.
          </UI.Message>
        </UI.Card>
      )
    }

    if (loadingState) {
      return (
        <UI.Card>
          <Container>
            <Loader
              active
              size="massive"
              inline="centered"
              indeterminate={indeterminate}
            >
              <UI.Text center>{loadingState}</UI.Text>

              {indeterminate && (
                <UI.Text center size="small">
                  Your purchase is taking longer than expected. We will update
                  this page as soon as it is ready.
                </UI.Text>
              )}
            </Loader>
          </Container>
        </UI.Card>
      )
    }

    return (
      <UI.Card>
        <UI.Text center>
          <UI.Text size="huge">
            You've reserved {formatUSD(purchase.tranche.priceUSD)} worth of{' '}
            {storefront.tokenName}
          </UI.Text>
          <UI.Text>How much would you like to purchase?</UI.Text>

          <UI.DollarField
            placeholder={Math.floor(purchase.cost * purchase.exchangeRate)}
            name="purchaseAmount"
            labelPosition="right"
            type="text"
            value={this.state.dollarValue}
            onChange={this.handleChange}
          />

          <UI.Text>
            you will receive{' '}
            {Math.floor(this.state.dollarValue / purchase.tranche.costPerToken)}{' '}
            {storefront.name} tokens
          </UI.Text>

          <UI.Text>
            Token price: {formatUSD(purchase.tranche.costPerToken)} USD
          </UI.Text>
          <UI.Divider hidden />

          <UI.Text weight="bold" size="huge">
            Send{' '}
            {formatCoin(
              this.state.dollarValue / purchase.exchangeRate,
              purchase.currency
            )}{' '}
            to
          </UI.Text>

          {process.env.REACT_APP_TESTNET !== 'false' && (
            <UI.Text weight="bold">
              Send RINKEBY Testnet ETH or Testnet BTC ONLY
            </UI.Text>
          )}

          <UI.PaymentDetails address={paymentAddress} memo={paymentMemo} />
        </UI.Text>

        <UI.Divider hidden />

        {purchase &&
          purchase.paymentAddress &&
          purchase.currency === 'ETH' && (
            <UI.QRCode
              to={purchase.paymentAddress}
              value={Number(purchase.cost * 10e18 || 0)}
            />
          )}

        <UI.Divider hidden />

        {purchase.expirationTime && (
          <UI.Text center>
            Invoice expires in{' '}
            <UI.Countdown simple to={purchase.expirationTime} /> |{' '}
            <UI.Link onClick={handleClickCancel}>Go back</UI.Link>
          </UI.Text>
        )}
      </UI.Card>
    )
  }
}

const selectors = createStructuredSelector({})

const actions = (dispatch, { params, firebase }) => ({
  handleClickCancel: () => dispatch(routerActions.push('/')),

  purchaseNotFound: () => {
    analytics.timestampPurchase(params.purchaseId, 'notFound')
    throw new Error(
      `Purchase resorted to purchase not found screen [${params.purchaseId}]`
    )
  },

  manuallyFetchPurchase: async () => {
    const event = 'value'
    const path = `purchases/${params.purchaseId}`

    // First remove any watchers on the event to prevent any caching of permission issues
    firebase.unWatchEvent(event, path)
    // Manually watch the event on the next function ticket, after any caches have been cleared
    setTimeout(() => firebase.watchEvent(event, path))
  },

  purchaseUpdated: (loadingState, purchase, invoiceId) => {
    if (loadingState)
      analytics.timestampPurchase(params.purchaseId, loadingState)

    dispatch({
      type: 'PURCHASE_UPDATED',
      track: {
        loadingState,
        purchase,
        purchaseId: params.purchaseId,
        invoiceId,
        timestamp: new Date().getTime(),
      },
    })
  },

  addressDisplayed: (purchase, invoiceId) => {
    analytics.timestampPurchase(params.purchaseId, 'addressDisplayed')

    dispatch({
      type: 'ADDRESS_DISPLAYED',
      track: {
        purchase,
        purchaseId: params.purchaseId,
        invoiceId,
        timestamp: new Date().getTime(),
      },
    })
  },

  redirectToReceipt: invoiceId => {
    analytics.timestampPurchase(params.purchaseId, 'receiptShown')
    dispatch({
      type: 'RECEIPT_SHOWN',
      track: {
        purchaseId: params.purchaseId,
        invoiceId,
        timestamp: new Date().getTime(),
      },
    })
    dispatch(routerActions.push(`/purchase/${params.purchaseId}/receipt`))
  },

  redirectToExpired: invoiceId => {
    analytics.timestampPurchase(params.purchaseId, 'expiredShown')
    dispatch({
      type: 'RECEIPT_SHOWN',
      track: {
        purchaseId: params.purchaseId,
        invoiceId,
        timestamp: new Date().getTime(),
      },
    })
    dispatch(routerActions.push(`/purchase/${params.purchaseId}/expired`))
  },
})

export default compose(connect(selectors, actions), userIsAuthenticated())(
  Invoice
)



// WEBPACK FOOTER //
// ./src/screens/Invoice.js